import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField, Textarea } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs.jsx';
import { useCurrencyStore } from '@/stores/currency.js';

export default function CompanySettingsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({ queryKey: ['companySettings'], queryFn: () => api.get('/settings/company') });
  const form = useForm({ defaultValues: {} });

  useEffect(() => {
    if (data) {
      form.reset(data);
      useCurrencyStore.setState({
        currency: data.currency || 'LKR',
        currencySymbol: data.currency_symbol || 'Rs.',
      });
    }
  }, [data]); // eslint-disable-line

  const save = useMutation({
    mutationFn: (v) => api.put('/settings/company', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companySettings'] });
      toast({ variant: 'success', title: 'Settings saved' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Save failed', description: e.message }),
  });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('logo', file);
    try {
      await api.post('/settings/company/logo', fd, { headers: {} });
      qc.invalidateQueries({ queryKey: ['companySettings'] });
      toast({ variant: 'success', title: 'Logo uploaded' });
    } catch (err) { toast({ variant: 'error', title: 'Upload failed', description: err.message }); }
  };

  if (isLoading) return null;

  return (
    <div className="space-y-5">
      <PageHeader title="Company Settings" description="Organization profile and payroll defaults" />

      <form onSubmit={form.handleSubmit((v) => save.mutate({
        ...v,
        smtp_port: v.smtp_port ? Number(v.smtp_port) : null,
      }))}>
        <Tabs defaultValue="org">
          <TabsList>
            <TabsTrigger value="org">Organization</TabsTrigger>
            <TabsTrigger value="payroll">Payroll Defaults</TabsTrigger>
            <TabsTrigger value="bank">Bank</TabsTrigger>
            <TabsTrigger value="email">SMTP</TabsTrigger>
            <TabsTrigger value="logo">Logo</TabsTrigger>
          </TabsList>

          <TabsContent value="org">
            <Section title="Organization Profile">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="Company Name" required><Input {...form.register('name', { required: true })} /></FormField>
                <FormField label="Business Reg. No"><Input {...form.register('br_no')} /></FormField>
                <FormField label="EPF No"><Input {...form.register('epf_no')} /></FormField>
                <FormField label="ETF No"><Input {...form.register('etf_no')} /></FormField>
                <FormField label="Currency"><Input {...form.register('currency')} /></FormField>
                <FormField label="Currency Symbol"><Input {...form.register('currency_symbol')} /></FormField>
                <FormField label="Address" className="md:col-span-2"><Textarea {...form.register('address')} /></FormField>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="payroll">
            <Section title="Payroll Defaults">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="OT Base Method">
                  <Select {...form.register('ot_base_method')}>
                    <option value="basic_div_240">Basic ÷ 240</option>
                    <option value="gross_div_240">Gross ÷ 240</option>
                  </Select>
                </FormField>
                <FormField label="Default OT Multiplier">
                  <Input type="number" step="0.1" min="1" {...form.register('ot_default_multiplier', { valueAsNumber: true })} />
                </FormField>
                <FormField label="Holiday OT Multiplier">
                  <Input type="number" step="0.1" min="1" {...form.register('holiday_ot_multiplier', { valueAsNumber: true })} />
                </FormField>
                <FormField label="No-Pay Divisor">
                  <Select {...form.register('nopay_method')}>
                    <option value="working_days">Working Days in Month</option>
                    <option value="fixed_30">Fixed 30</option>
                  </Select>
                </FormField>
              </div>
            </Section>
            <Section title="Short Leave Policy">
              <div className="grid gap-4 md:grid-cols-3">
                <FormField label="Max Incidents / Month" hint="0 = disabled">
                  <Input type="number" min={0} step={1} {...form.register('short_leave_max_per_month', { valueAsNumber: true })} />
                </FormField>
                <FormField label="Max Hours / Month" hint="0 = no cap">
                  <Input type="number" min={0} step={0.25} {...form.register('short_leave_max_hours_per_month', { valueAsNumber: true })} />
                </FormField>
                <FormField label="Max Hours / Incident">
                  <Input type="number" min={0} step={0.25} {...form.register('short_leave_max_hours_per_incident', { valueAsNumber: true })} />
                </FormField>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="bank">
            <Section title="Bank — Originator">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="Bank Name"><Input {...form.register('bank_name')} /></FormField>
                <FormField label="Branch"><Input {...form.register('bank_branch')} /></FormField>
                <FormField label="Account No"><Input {...form.register('bank_account')} /></FormField>
                <FormField label="Originator ID (SLIPS)"><Input {...form.register('bank_originator_id')} /></FormField>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="email">
            <Section title="SMTP (overrides .env)">
              <div className="grid gap-4 md:grid-cols-2">
                <FormField label="Host"><Input {...form.register('smtp_host')} /></FormField>
                <FormField label="Port"><Input type="number" {...form.register('smtp_port')} /></FormField>
                <FormField label="User"><Input {...form.register('smtp_user')} /></FormField>
                <FormField label="Password"><Input type="password" {...form.register('smtp_pass')} /></FormField>
                <FormField label="From Address" className="md:col-span-2"><Input {...form.register('smtp_from')} /></FormField>
              </div>
            </Section>
          </TabsContent>

          <TabsContent value="logo">
            <Section title="Company Logo">
              {data?.logo_path && <img src={data.logo_path} alt="logo" className="mb-3 h-20 rounded border bg-white p-2" />}
              <input type="file" accept="image/*" onChange={uploadLogo} />
              <p className="mt-2 text-xs text-slate-500">PNG or JPG, max 2 MB.</p>
            </Section>
          </TabsContent>
        </Tabs>

        <div className="mt-5 flex justify-end">
          <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Settings'}</Button>
        </div>
      </form>
    </div>
  );
}